/**
 * Reversi (Othello) Game Engine
 *
 * Core game logic: board setup, move validation, disc flipping,
 * turn management, game-over detection, scoring, serialization.
 */

import {
  CellState,
  PlayerColor,
  Position,
  ReversiMove,
  MoveResult,
  ReversiGameState,
  ReversiGameSettings,
  BOARD_SIZE,
  DIRECTIONS,
} from './types';

export class ReversiEngine {
  private state: ReversiGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  /**
   * Initialize a new game with the given settings.
   * Sets up the 8×8 board with 4 center discs in the standard pattern.
   */
  initializeGame(settings: ReversiGameSettings): void {
    const board = this.createEmptyBoard();

    // Standard starting position: 2 white, 2 black in center diagonal
    const mid = BOARD_SIZE / 2;
    board[mid - 1][mid - 1] = 'white';
    board[mid - 1][mid] = 'black';
    board[mid][mid - 1] = 'black';
    board[mid][mid] = 'white';

    const validMoves = this.computeValidMoves(board, 'black');

    this.state = {
      board,
      currentPlayer: 'black',
      moveHistory: [],
      gameStatus: 'active',
      score: { black: 2, white: 2 },
      validMoves,
      lastFlipped: [],
      gameId: `reversi-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
      skippedTurns: 0,
    };
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  /**
   * Get all valid moves for a player on the current board.
   */
  getValidMoves(player?: PlayerColor): Position[] {
    const p = player || this.state.currentPlayer;
    return this.computeValidMoves(this.state.board, p);
  }

  /**
   * Check if a move is valid for the given player at the given position.
   */
  isValidMove(player: PlayerColor, position: Position): boolean {
    return this.getFlippableDiscs(player, position).length > 0;
  }

  /**
   * Get the discs that would be flipped if the player places at position.
   */
  getFlippableDiscs(player: PlayerColor, position: Position): Position[] {
    return this.computeFlippableDiscs(this.state.board, player, position);
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  /**
   * Execute a move: place a disc and flip all outflanked discs.
   * Returns the MoveResult with flipped discs, new board, and next valid moves.
   */
  executeMove(player: PlayerColor, position: Position): MoveResult {
    if (this.state.gameStatus !== 'active') {
      throw new Error('Game is not active');
    }

    if (player !== this.state.currentPlayer) {
      throw new Error(`Not ${player}'s turn`);
    }

    const flippable = this.computeFlippableDiscs(this.state.board, player, position);
    if (flippable.length === 0) {
      throw new Error('Invalid move: no discs to flip');
    }

    // Clone the board
    const newBoard = this.state.board.map(row => [...row]);

    // Place the disc
    newBoard[position.row][position.col] = player;

    // Flip all outflanked discs
    for (const disc of flippable) {
      newBoard[disc.row][disc.col] = player;
    }

    // Create the move record
    const move: ReversiMove = {
      player,
      position,
      timestamp: Date.now(),
    };

    // Determine next player
    const opponent = this.getOpponent(player);
    const nextValidMoves = this.computeValidMoves(newBoard, opponent);

    // Update state
    this.state.board = newBoard;
    this.state.moveHistory.push(move);
    this.state.lastFlipped = flippable;
    this.state.score = this.computeScore(newBoard);
    this.state.skippedTurns = 0;

    return {
      flippedDiscs: flippable,
      newBoard,
      validMoves: nextValidMoves,
      move,
    };
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  /**
   * Advance to the next player's turn.
   * If the next player has no valid moves, skip their turn.
   * If neither player can move, the game is over.
   */
  advanceTurn(): void {
    if (this.state.gameStatus !== 'active') return;

    const opponent = this.getOpponent(this.state.currentPlayer);
    const opponentMoves = this.computeValidMoves(this.state.board, opponent);

    if (opponentMoves.length > 0) {
      // Opponent can play
      this.state.currentPlayer = opponent;
      this.state.validMoves = opponentMoves;
      this.state.skippedTurns = 0;
      return;
    }

    // Opponent has no moves — check if current player can still play
    const currentMoves = this.computeValidMoves(this.state.board, this.state.currentPlayer);

    if (currentMoves.length > 0) {
      // Current player plays again (opponent's turn is skipped)
      this.state.validMoves = currentMoves;
      this.state.skippedTurns++;
      return;
    }

    // Neither player can move — game over
    this.endGame();
  }

  // =========================================================================
  // Game Over
  // =========================================================================

  /**
   * Check if the game is over.
   */
  isGameOver(): boolean {
    return this.state.gameStatus === 'completed';
  }

  /**
   * Get the current score (disc count for each player).
   */
  getScore(): { black: number; white: number } {
    return { ...this.state.score };
  }

  /**
   * Get the winner. Returns the player with more discs, 'draw', or undefined.
   */
  getWinner(): PlayerColor | 'draw' | undefined {
    return this.state.winner;
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getBoard(): CellState[][] {
    return this.state.board.map(row => [...row]);
  }

  getCurrentPlayer(): PlayerColor {
    return this.state.currentPlayer;
  }

  getGameState(): ReversiGameState {
    return {
      ...this.state,
      board: this.state.board.map(row => [...row]),
      moveHistory: [...this.state.moveHistory],
      validMoves: [...this.state.validMoves],
      lastFlipped: [...this.state.lastFlipped],
      score: { ...this.state.score },
    };
  }

  getMoveHistory(): ReversiMove[] {
    return [...this.state.moveHistory];
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  /** Serialize the complete game state to JSON string */
  serialize(): string {
    return JSON.stringify(this.state);
  }

  /** Deserialize a JSON string to game state */
  deserialize(json: string): ReversiGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      if (!parsed.board || !Array.isArray(parsed.board)) {
        return { error: 'Invalid state: missing board' };
      }
      if (parsed.board.length !== BOARD_SIZE) {
        return { error: 'Invalid state: board must be 8x8' };
      }
      if (!['black', 'white'].includes(parsed.currentPlayer)) {
        return { error: 'Invalid state: invalid currentPlayer' };
      }
      if (!['active', 'completed'].includes(parsed.gameStatus)) {
        return { error: 'Invalid state: invalid gameStatus' };
      }

      return parsed as ReversiGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  /** Set the game state directly (for state restoration) */
  setGameState(state: ReversiGameState): void {
    this.state = {
      ...state,
      board: state.board.map(row => [...row]),
      moveHistory: [...state.moveHistory],
      validMoves: [...state.validMoves],
      lastFlipped: [...state.lastFlipped],
      score: { ...state.score },
    };
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  private createEmptyBoard(): CellState[][] {
    return Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => 'empty' as CellState)
    );
  }

  private createEmptyState(): ReversiGameState {
    return {
      board: this.createEmptyBoard(),
      currentPlayer: 'black',
      moveHistory: [],
      gameStatus: 'active',
      score: { black: 0, white: 0 },
      validMoves: [],
      lastFlipped: [],
      gameId: '',
      startedAt: 0,
      skippedTurns: 0,
    };
  }

  private getOpponent(player: PlayerColor): PlayerColor {
    return player === 'black' ? 'white' : 'black';
  }

  /**
   * Compute all valid moves for a player on a given board.
   */
  private computeValidMoves(board: CellState[][], player: PlayerColor): Position[] {
    const moves: Position[] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (board[row][col] === 'empty') {
          const flippable = this.computeFlippableDiscs(board, player, { row, col });
          if (flippable.length > 0) {
            moves.push({ row, col });
          }
        }
      }
    }
    return moves;
  }

  /**
   * Compute which discs would be flipped if player places at position on the given board.
   */
  private computeFlippableDiscs(
    board: CellState[][],
    player: PlayerColor,
    position: Position
  ): Position[] {
    const { row, col } = position;

    // Must be an empty cell
    if (row < 0 || row >= BOARD_SIZE || col < 0 || col >= BOARD_SIZE) {
      return [];
    }
    if (board[row][col] !== 'empty') {
      return [];
    }

    const opponent = this.getOpponent(player);
    const allFlipped: Position[] = [];

    for (const [dr, dc] of DIRECTIONS) {
      const flippedInDirection: Position[] = [];
      let r = row + dr;
      let c = col + dc;

      // Walk in this direction, collecting opponent discs
      while (
        r >= 0 && r < BOARD_SIZE &&
        c >= 0 && c < BOARD_SIZE &&
        board[r][c] === opponent
      ) {
        flippedInDirection.push({ row: r, col: c });
        r += dr;
        c += dc;
      }

      // If we ended on a disc of our own color, all collected discs are flipped
      if (
        flippedInDirection.length > 0 &&
        r >= 0 && r < BOARD_SIZE &&
        c >= 0 && c < BOARD_SIZE &&
        board[r][c] === player
      ) {
        allFlipped.push(...flippedInDirection);
      }
    }

    return allFlipped;
  }

  /**
   * Compute the score (disc count) for each player.
   */
  private computeScore(board: CellState[][]): { black: number; white: number } {
    let black = 0;
    let white = 0;
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (board[row][col] === 'black') black++;
        else if (board[row][col] === 'white') white++;
      }
    }
    return { black, white };
  }

  /**
   * End the game and determine the winner.
   */
  private endGame(): void {
    this.state.gameStatus = 'completed';
    this.state.validMoves = [];
    const { black, white } = this.state.score;

    if (black > white) {
      this.state.winner = 'black';
    } else if (white > black) {
      this.state.winner = 'white';
    } else {
      this.state.winner = 'draw';
    }
  }
}
